import { Helmet } from 'react-helmet-async'

import {
  Box, Button, Columns, LoadingModule, SaveFormModule,
} from '@domain-group/suit-theme'

import { useNumericParams } from '@domain-group/react-hooks'

import CampaignTeam from '@components/CampaignTeam'

import handleCallbackAction from '@functions/handleCallbackAction'
import { getCampaignPermissions, getCampaignType } from '@functions/campaign'

import CampaignInspections from '@components/CampaignInspections'
import Documents from '@components/Documents'
import NotificationBannerLocation from '@components/NotificationBannerLocation'
import NotificationModuleLocation from '@components/NotificationModuleLocation'
import PageHeader from '@components/PageHeader'
import ToursAndLinks from '@components/ToursAndLinks'

import useCampaign, { useCampaignForm } from '@hooks/useCampaign'
import useCurrentUser from '@hooks/useCurrentUser'
import useEntityCustomFieldsForm from '@hooks/useEntityCustomFieldsForm'

import propertyDetailsTabBarItems from '@sections/Client/packs/Campaign/utils/generatePropertyDetailsTabBarItems'

import AgencyMarketing from '../AgencyMarketing'
import Details from '../Details'
import PropertyLocation from '../PropertyLocation'

const PropertyDetails = () => {
  const { campaignId } = useNumericParams()

  const campaignPayload = useCampaign(
    { id: campaignId },
  )

  const {
    agency,
    callbacks: {
      assignCampaignAgents,
      updateCampaign: updateFn,
    },
    campaign,
    campaign: { address, agency_marketing, id },
    loading,
    updating,
  } = campaignPayload

  const campaignFormPayload = useCampaignForm(campaign, { validateOn: 'change' })
  const {
    entityState,
    saveEnabled,
  } = campaignFormPayload

  const { currentUser } = useCurrentUser()

  const campaignPermissions = getCampaignPermissions({ user: currentUser, campaignType: getCampaignType(campaign) })

  const customFieldsForm = useEntityCustomFieldsForm({
    // @ts-expect-error TS2741
    entity: campaign,
    options: {
      // @ts-expect-error TS2740
      agency,
      performHttpRequests: !agency_marketing,
    },
    // @ts-expect-error TS2339
    deps: [campaign.id, agency.id],
  })

  const { callbacks: { buildNestedValues } } = customFieldsForm

  const updateCampaignPayload = {
    callbacks: {
      action: updateFn,
    },
    entityParams: { ...entityState, data_store_item_values: buildNestedValues() },
    requestOptions: {
      include_campaign_data_store_items: true,
    },
    toastText: 'Campaign Updated',
  }

  return (
    <>
      <Helmet>
        <title>{address || 'Campaign'} | Property Details</title>
      </Helmet>

      <PageHeader
        activeTabBarItemKey="details"
        boxProps={{ height: [112, 105], justifyContent: 'flex-start' }}
        nestedNavigation
        showBackButton
        tabBarItems={propertyDetailsTabBarItems(id, campaignPermissions)}
        title="Property Details"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }}>
          <Columns.Main>
            <NotificationBannerLocation locationKey="agency.campaign.details.banner" />

            <LoadingModule loading={loading} times={3} />

            {!agency_marketing && !loading && (
              <Details
                campaign={campaign}
                campaignFormPayload={campaignFormPayload}
                customFieldsForm={customFieldsForm}
              />
            )}

            {agency_marketing && !loading && <AgencyMarketing campaignFormPayload={campaignFormPayload} />}

            <SaveFormModule>
              <Button
                buttonStyle="primaryCreate"
                disabled={!saveEnabled}
                loading={updating}
                // @ts-expect-error TS2322
                marginLeft="auto"
                // @ts-expect-error TS2345
                onClick={() => handleCallbackAction(updateCampaignPayload)}
                size="medium"
              >
                Save Details
              </Button>
            </SaveFormModule>
          </Columns.Main>

          <Columns.Sidebar>
            <NotificationModuleLocation locationKey="agency.campaign.details.sidebar" />

            <CampaignTeam
              callbacks={{ manageTeam: assignCampaignAgents }}
              campaign={campaign}
            />

            {!agency_marketing && (
              <>
                <PropertyLocation campaignPayload={campaignPayload} />

                <CampaignInspections campaign={campaign} />

                <ToursAndLinks campaign={campaign} />

                <Documents campaign={campaign} />
              </>
            )}
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PropertyDetails
